import { useEffect, useRef, useState } from 'react';
import { Button, Modal } from '../components/ui';
import type { Session } from './RecordingPlayer';
import { sessionName, prettyStamp } from './RecordingPlayer';

/**
 * "AI Generate…": a piece of writing made from the recording's transcript by
 * Claude, to instructions typed here. The text streams into this dialog and
 * stays in the browser: Copy, save as .txt, or save as .pdf. The hub keeps
 * nothing of it (it audits that a generation was asked for, and the first
 * line of the instructions).
 */

const PRESETS: { label: string; text: string }[] = [
  { label: 'Press release', text: 'Write a 500-word press release about this event for local media. Lead with the result or the main news, include two or three quotes taken from what was said (attributed as "a speaker" unless a name is clear from the transcript), and end with a short boilerplate line about the organisation.' },
  { label: 'Match report', text: 'Write a match report of about 600 words in the style of a sports news site: the result, how the game unfolded with the key moments and the time each happened (HH:MM:SS into the recording), standout performers, and what it means next.' },
  { label: 'Key moments', text: 'List the key moments of this recording in order, each with its time into the recording (HH:MM:SS) and one sentence saying what happened. Aim for 15 to 30 moments. Mark the five most important with a star.' },
  { label: 'Social posts', text: 'Write six social media posts about this event: two for Facebook (up to 80 words each), two for Instagram captions (with hashtags), and two for X (under 280 characters each). Vary the angle: result, a standout moment, a quote, thanks to the crowd.' },
  { label: 'Summary', text: 'Summarise what was said in this recording in about 300 words, in plain language, for someone who was not there. Then add a short bulleted list of decisions or announcements, each with its time into the recording.' },
  { label: 'Meeting minutes', text: 'Write minutes of this meeting: attendees as far as they can be told from the transcript, the items discussed in order with a short note on each, decisions made, and actions with who was asked to do them. Give the time into the recording (HH:MM:SS) where each item starts.' },
];

type Phase = 'idle' | 'running' | 'done' | 'failed';

export function GenerateDialog({ session, open, onClose }: { session: Session; open: boolean; onClose: () => void }) {
  const [instructions, setInstructions] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [text, setText] = useState('');
  const [message, setMessage] = useState('');
  const [usage, setUsage] = useState<{ inputTokens: number; outputTokens: number; model: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const outRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => { if (phase === 'running' && outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight; }, [text, phase]);

  const title = `${sessionName(session)} ${prettyStamp(session.session).slice(0, 10)}`;
  const fileStem = title.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'recording';

  async function generate() {
    if (instructions.trim().length < 3) return;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setPhase('running');
    setText('');
    setMessage('');
    setUsage(null);
    try {
      const res = await fetch(`/api/recordings/${session.instanceId}/${session.session}/generate`, {
        method: 'POST', credentials: 'include', signal: controller.signal,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ instructions }),
      });
      if (!res.ok || !res.body) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? `The hub answered ${res.status}.`);
      }
      // Server-sent events, read by hand: "data: {...}\n\n" frames.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finished = false;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let at: number;
        while ((at = buffer.indexOf('\n\n')) >= 0) {
          const frame = buffer.slice(0, at);
          buffer = buffer.slice(at + 2);
          const line = frame.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          const msg = JSON.parse(line.slice(6)) as { text?: string; done?: boolean; usage?: typeof usage; error?: string };
          if (msg.text) setText((t) => t + msg.text);
          if (msg.error) throw new Error(msg.error);
          if (msg.done) { finished = true; setUsage(msg.usage ?? null); }
        }
      }
      if (!finished) throw new Error('The connection ended before the answer was complete.');
      setPhase('done');
    } catch (err) {
      if (controller.signal.aborted) { setPhase('idle'); return; }
      setMessage(err instanceof Error ? err.message : 'Generation failed.');
      setPhase('failed');
    }
  }

  function stop() {
    abort.current?.abort();
    setPhase(text ? 'done' : 'idle');
  }

  async function copy() {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { setMessage('Could not copy.'); }
  }

  function saveTxt() {
    const blob = new Blob([`${title}\n\n${text}\n`], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${fileStem}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  async function savePdf() {
    // jsPDF is loaded only when a PDF is asked for; the page never pays for it otherwise.
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 56;
    const width = doc.internal.pageSize.getWidth() - margin * 2;
    const height = doc.internal.pageSize.getHeight();
    doc.setFont('helvetica', 'bold').setFontSize(14);
    doc.text(title, margin, margin);
    doc.setFont('helvetica', 'normal').setFontSize(11);
    let y = margin + 26;
    for (const para of text.replace(/\r/g, '').split('\n')) {
      const heading = /^#{1,3}\s+/.test(para);
      const line = para.replace(/^#{1,3}\s+/, '').replace(/\*\*(.+?)\*\*/g, '$1');
      doc.setFont('helvetica', heading ? 'bold' : 'normal');
      const wrapped = line ? (doc.splitTextToSize(line, width) as string[]) : [''];
      for (const w of wrapped) {
        if (y > height - margin) { doc.addPage(); y = margin; }
        doc.text(w, margin, y);
        y += 15;
      }
    }
    doc.save(`${fileStem}.pdf`);
  }

  const running = phase === 'running';

  return (
    <Modal open={open} onClose={() => { if (!running) onClose(); }} title={`AI Generate: ${title}`} width="max-w-3xl"
      footer={
        <>
          {text && phase !== 'running' && (
            <>
              <Button variant="ghost" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy'}</Button>
              <Button variant="ghost" onClick={saveTxt}>Save .txt</Button>
              <Button variant="ghost" onClick={() => void savePdf()}>Save .pdf</Button>
            </>
          )}
          <div className="flex-1" />
          {running
            ? <Button variant="ghost" onClick={stop}>Stop</Button>
            : <Button variant="ghost" onClick={onClose}>Close</Button>}
          <Button variant="primary" disabled={running || instructions.trim().length < 3} loading={running} onClick={() => void generate()}>
            {running ? 'Writing…' : text ? 'Generate again' : 'Generate'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button key={p.label} type="button" disabled={running}
                    className="rounded-full border border-grid px-2.5 py-1 text-xs text-ink-secondary hover:bg-raised hover:text-ink disabled:opacity-40"
                    onClick={() => setInstructions(p.text)}>
              {p.label}
            </button>
          ))}
        </div>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          disabled={running}
          rows={4}
          maxLength={4000}
          placeholder='What to write, and any context. For example: "Create a 500 word press release on this game. Home side is the Magpies, the final was 24-17."'
          className="w-full rounded border border-grid bg-plane px-3 py-2 text-sm text-ink placeholder:text-ink-muted"
        />
        <p className="text-xs text-ink-muted">
          The transcript, with its timecodes, is sent to Anthropic with these instructions. The result appears here and is kept nowhere on the hub: copy it or save it before closing.
        </p>

        {(text || running || phase === 'failed') && (
          <div ref={outRef} className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap rounded border border-grid bg-plane px-4 py-3 text-sm leading-relaxed">
            {text}
            {running && <span className="ml-0.5 inline-block h-4 w-2 animate-pulse bg-series-1 align-text-bottom" />}
          </div>
        )}
        {message && <p className="text-xs text-bad">{message}</p>}
        {usage && phase === 'done' && (
          <p className="text-xs text-ink-muted">
            {usage.model} · {Math.round(usage.inputTokens / 1000)}k tokens in, {usage.outputTokens} out.
          </p>
        )}
      </div>
    </Modal>
  );
}
